import { Check, Copy } from 'lucide-react';
import { type ReactNode, useEffect, useRef, useState } from 'react';
import { Button } from './Button';

const KEYWORDS = new Set([
  'await',
  'async',
  'const',
  'let',
  'var',
  'if',
  'else',
  'try',
  'catch',
  'return',
  'function',
  'new',
  'true',
  'false',
  'null',
  'undefined',
]);

/** A light touch of colour for JavaScript and JSON: comments, strings and keywords. */
function highlight(code: string): ReactNode[] {
  const parts: ReactNode[] = [];
  const pattern =
    /(\/\/[^\n]*|#[^\n]*$|'(?:[^'\\\n]|\\.)*'|"(?:[^"\\\n]|\\.)*"|`(?:[^`\\]|\\.)*`|\b[a-z]+\b)/gm;
  let last = 0;
  for (const match of code.matchAll(pattern)) {
    const [text] = match;
    const at = match.index ?? 0;
    if (at > last) parts.push(code.slice(last, at));
    if (text.startsWith('//') || text.startsWith('#')) {
      parts.push(
        <span key={at} className="mz-code-comment">
          {text}
        </span>,
      );
    } else if (/^['"`]/.test(text)) {
      parts.push(
        <span key={at} className="mz-code-string">
          {text}
        </span>,
      );
    } else if (KEYWORDS.has(text)) {
      parts.push(
        <span key={at} className="mz-code-keyword">
          {text}
        </span>,
      );
    } else {
      parts.push(text);
    }
    last = at + text.length;
  }
  if (last < code.length) parts.push(code.slice(last));
  return parts;
}

/** Code to read or copy: a title (the file, or “Terminal”), the code, and a copy button. */
export function CodeBlock({
  code,
  title,
  plain,
}: {
  code: string;
  title?: string;
  /** No colours (a terminal's output, say). */
  plain?: boolean;
}) {
  const [copied, setCopied] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout>>(undefined);
  useEffect(() => () => clearTimeout(timer.current), []);

  async function copy() {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      clearTimeout(timer.current);
      timer.current = setTimeout(() => setCopied(false), 1600);
    } catch {
      // Some browsers refuse; the code stays selectable.
    }
  }

  return (
    <div className="mz-codeblock">
      <div className="mz-codeblock-head">
        <span>{title ?? 'Code'}</span>
        <Button
          variant="ghost"
          size="sm"
          icon={copied ? Check : Copy}
          label={copied ? 'Copied' : `Copy ${title ?? 'code'}`}
          onClick={copy}
        />
      </div>
      <pre>{plain ? code : highlight(code)}</pre>
    </div>
  );
}
